import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
import {Mail, Eye, EyeOff } from '../../assets/icons';
import psw from '../../assets/icons/password.svg'
import logo from '../../assets/icons/logo.svg';
import '../../assets/css/doctor/login.css'; // Reusing doctor login styles

// Material Design color palette
const materialColors = {
    primary: '#1976D2',
    secondary: '#424242',
    success: '#4CAF50',
    error: '#F44336',
    lightBg: '#F5F5F5',
    borderColor: '#E0E0E0',
    textPrimary: '#212121',
    textSecondary: '#757575'
};

const AHTLogin = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await login(formData.email, formData.password);
            navigate('/aht');
        } catch (err) {
            setError(err.message || 'Failed to login. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#fff',
            display: 'flex',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        }}>
            {/* Visual Section */}
            <div style={{
                flex: 1,
                backgroundColor: materialColors.primary,
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                '@media (min-width: 768px)': {
                    display: 'flex'
                }
            }}>
                <img 
                    src="/doctor-login.svg" 
                    alt="AHT Login" 
                    style={{
                        maxWidth: '100%',
                        height: 'auto'
                    }}
                />
            </div>

            {/* Form Section */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '48px 40px',
                maxWidth: '100%'
            }}>
                <div style={{
                    maxWidth: '400px',
                    margin: '0 auto',
                    width: '100%'
                }}>
                    {/* Header */}
                    <div style={{
                        marginBottom: '48px',
                        textAlign: 'center'
                    }}>
                        <img 
                            src={logo} 
                            alt="E-AquaHealth" 
                            style={{
                                height: '40px',
                                marginBottom: '24px'
                            }}
                        />
                        <h1 style={{
                            color: materialColors.textPrimary,
                            fontSize: '28px',
                            fontWeight: 500,
                            marginBottom: '8px',
                            marginTop: 0
                        }}>
                            AHT Login
                        </h1>
                        <p style={{
                            color: materialColors.textSecondary,
                            fontSize: '14px',
                            marginTop: '8px'
                        }}>
                            Welcome back! Please login to your account
                        </p>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div style={{
                            backgroundColor: '#FFEBEE',
                            border: `1px solid ${materialColors.error}`,
                            borderRadius: '4px',
                            padding: '16px',
                            marginBottom: '24px',
                            color: '#C62828',
                            fontSize: '14px',
                            animation: 'slideDown 0.3s ease'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <FormInput
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                            icon={<img src={Mail} alt="" />}
                        />

                        <div style={{ position: 'relative' }}>
                            <FormInput
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => setFormData({...formData, password: e.target.value})}
                                required
                                icon={<img src={psw} alt="" />}
                            />
                            <button 
                                type="button" 
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {showPassword ? <img src={EyeOff} alt="" /> : <img src={Eye} alt="" />}
                            </button>
                        </div>

                        {/* Remember & Forgot */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '14px'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: materialColors.textPrimary,
                                cursor: 'pointer'
                            }}>
                                <input 
                                    type="checkbox" 
                                    style={{
                                        marginRight: '8px',
                                        cursor: 'pointer'
                                    }}
                                />
                                Remember me
                            </label>
                            <Link 
                                to="/forgot-password"
                                style={{
                                    color: materialColors.primary,
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    transition: 'color 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#1565C0'}
                                onMouseLeave={(e) => e.target.style.color = materialColors.primary}
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        {/* Login Button */}
                        <Button
                            text={isLoading ? "Logging in..." : "Login"}
                            type="submit"
                            disabled={isLoading}
                            style={{
                                backgroundColor: materialColors.primary,
                                color: 'white',
                                padding: '12px 16px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                                opacity: isLoading ? 0.7 : 1,
                                marginTop: '8px'
                            }}
                        />
                    </form>

                    {/* Register Prompt */}
                    <div style={{
                        marginTop: '32px',
                        paddingTop: '32px',
                        borderTop: `1px solid ${materialColors.borderColor}`,
                        textAlign: 'center'
                    }}>
                        <p style={{
                            color: materialColors.textSecondary,
                            fontSize: '14px',
                            marginBottom: '8px'
                        }}>
                            Don't have an account?
                        </p>
                        <Link 
                            to="/aht/register"
                            style={{
                                color: materialColors.primary,
                                textDecoration: 'none',
                                fontWeight: 500,
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#1565C0'}
                            onMouseLeave={(e) => e.target.style.color = materialColors.primary}
                        >
                            Register as an Animal Health Technologist
                        </Link>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes slideDown {
                    from { 
                        opacity: 0; 
                        transform: translateY(-10px); 
                    }
                    to { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                
                @media (max-width: 768px) {
                    div:has(> img[alt="AHT Login"]) {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default AHTLogin;
